import React, { Component } from "react";
import { useDispatch } from "react-redux";
import PropTypes from "prop-types";
import classnames from "classnames";
import { v4 as uuidv4 } from "uuid";
import Base58 from "base-58";
import styled from "styled-components";
import { Popup } from "@witbooking/react-components";
import { evalScript } from "../../../utils/misc";
import { creditCardIcons } from "../../../theme/assets";
import styles from "../../../components/PaymentForm/PaymentForm.scss";
import {
  createCustomerToken,
  createPnPErrorLog,
  updateCustomerCreditCardData,
} from "../../../utils/witbookingApi";
import constants from "../../../utils/constants";
import media from "../../../utils/media";
import { setSmartPaymentId } from "../../../containers/BookingConfirmation/reducer";

const PnPV2Form = styled.div`
  height: auto;
`;

const StyledDescription = styled.div`
  color: #908f96;
  margin: 5px 0;
  text-align: left;
  font-size: 12px;

  ${media.up.md`
    font-size: 14px;
  `};
`;

const StyledIframe = styled.iframe`
  width: 100%;
  height: 87vh;

  & .vertical-scroll {
    height: 100% !important;
  }
`;

function CreditCardIcons({ creditCardTypes }) {
  return (
    <div className={classnames("wbkv9-OnlinePayment-creditCardIcons", styles.creditCardIcons)}>
      {creditCardTypes.map((creditCard, idx) => (
        <div
          key={idx}
          className={classnames(
            "wbkv9-OnlinePayment-creditCardIconContainer",
            styles.creditCardIconContainer
          )}>
          <img src={creditCardIcons[creditCard]} alt={creditCard} />
        </div>
      ))}
    </div>
  );
}

function generateBase58uuid() {
  return Base58.encode(new Buffer(uuidv4())).substr(0, 21);
}

/**
 * Steps:
 * - Load PnP's javascript SDK.
 * - Catch "paylandsLoaded" event and create customer token.
 * - Initialize PnP form with customer token.
 * - When pending reservation is done, tokenize credit card and save it
 * in PnP system.
 * - Catch "savedCard" event and update customer data with tokenized card
 * - Create payment order and start payment's process
 */
class PayNoPainV2Bridge extends Component {
  constructor(props) {
    super(props);
    this.createdCustomerToken = false;
    this.savedTokenizedCard = false;
    this.handleCloseModal = this.handleCloseModal.bind(this);
  }
  componentDidMount() {
    // Javascript SDK is loaded and variable paylands is available
    window.addEventListener("paylandsLoaded", this.initializePaylands);

    // Credit card has been tokenized successfully
    window.addEventListener("savedCard", this.savedCard);

    // There was an error tokenizing credit card.
    // In the past, the event was "errorServer" (and currently is still working) but now in the paylands
    // documentation it says that the event is called "error" so we listen to both but "error"
    // will only call to API endpoint to log.
    window.addEventListener("errorServer", this.errorServer);

    if (!window.paylands) {
      // FIXME load from configuration
      // Load SDK
      const scriptURL = "https://api.paylands.com/js/v1-iframe.js";
      evalScript({
        scriptArr: [{ src: scriptURL }],
        cb: () => {},
      });
    } else {
      const paylandsLoadedEvent = new Event("paylandsLoaded");
      window.dispatchEvent(paylandsLoadedEvent);
    }

    if (this.props.match && this.props.match.params && this.props.match.params.reservationId) {
      if (this.props.match.params.status) {
        switch (this.props.match.params.status) {
          case constants.PAYMENT_STATUS.SUCCESS:
            window.parent.postMessage(
              {
                propertyTicker: this.props.match.params.hotelTicker,
                reservationId: this.props.match.params.reservationId,
                status: constants.PAYMENT_STATUS.SUCCESS,
              },
              "*"
            );
            break;

          case constants.PAYMENT_STATUS.FAILURE:
            window.parent.postMessage(
              {
                propertyTicker: this.props.match.params.hotelTicker,
                reservationId: this.props.match.params.reservationId,
                status: constants.PAYMENT_STATUS.FAILURE,
              },
              "*"
            );
            break;

          default:
            break;
        }
      }
    }

    const eventMethod = window.addEventListener ? "addEventListener" : "attachEvent";
    const eventer = window[eventMethod];
    const messageEvent = eventMethod === "attachEvent" ? "onmessage" : "message";

    // Listen to message from child window
    eventer(messageEvent, event => {
      if (event.data) {
        switch (event.data.status) {
          case constants.PAYMENT_STATUS.SUCCESS:
            this.props.handleCloseModal();
            this.props.createPnPV2PaymentOrderSuccess();
            break;

          case constants.PAYMENT_STATUS.FAILURE:
            this.props.handleCloseModal();
            this.props.createPnPV2PaymentOrderError(true);
            this.savedTokenizedCard = false;
            break;

          default:
            break;
        }
      }
    });
  }

  componentDidUpdate(prevProps) {
    if (this.props.idReservation && this.props.idReservation !== prevProps.idReservation) {
      this.reservationId = this.props.idReservation;
      if (this.props.inSmartPayment) {
        this.props.dispatch(setSmartPaymentId(null));
      }

      // This is an optional prop for API
      window.paylands.setAdditional(this.reservationId);

      const validateCard =
        !window.__additionalData.PSD2_ACTIVE && this.props.chargeDeposit === true;
      // Tokenize and save credit card in PnP system
      if (validateCard) {
        window.paylands.storeSourceCard(true, this.props.service);
      } else {
        window.paylands.storeSourceCard();
      }
    }

    // Fallback when payment has 3DS
    if (
      this.props.pnpV2PaymentToken &&
      (!prevProps.pnpV2PaymentToken ||
        (prevProps.pnpV2PaymentToken &&
          prevProps.pnpV2PaymentToken.redirectionURL !==
            this.props.pnpV2PaymentToken.redirectionURL))
    ) {
      this.props.handleModalContent(
        <Popup closeWindow={this.handleCloseModal}>
          <StyledIframe src={this.props.pnpV2PaymentToken.redirectionURL} />
        </Popup>,
        {
          shouldCloseOnOverlayClick: false,
          hideCloseButton: true,
          style: { height: 530 },
          handleClose: this.handleCloseModal(),
        }
      );
    }
  }

  componentWillUnmount() {
    window.removeEventListener("paylandsLoaded", this.initializePaylands);
    window.removeEventListener("savedCard", this.savedCard);
    window.removeEventListener("errorServer", this.errorServer);
  }

  handleCloseModal() {
    this.savedTokenizedCard = false;
    this.props.handleCloseModal();
  }

  initializePaylands = () => {
    if (window.paylands && !this.createdCustomerToken && !this.props.pnpV2PaymentToken) {
      const customerId = generateBase58uuid();
      createCustomerToken(
        {},
        {
          propertyTicker: this.props.propertyTicker,
          customerId,
          paymentTicker: this.props.paymentTicker,
        }
      ).then(response => {
        if (response) {
          this.createdCustomerToken = true;

          if (
            window.__additionalData &&
            (window.__additionalData.ENVIRONMENT === constants.ENVIRONMENT.STAGING ||
              window.__additionalData.ENVIRONMENT === constants.ENVIRONMENT.DEVEL)
          ) {
            window.paylands.setMode("sandbox");
          }

          // Code for Witbooking's custom form
          window.paylands.setTemplate(this.props.template);

          // Loads PnP form with customer token
          window.paylands.initializate(response.token, "PnPV2-form");
        }
      });
    }
  };

  savedCard = event => {
    console.info("---- savedCard ----");
    if (event.data && !this.savedTokenizedCard) {
      console.info("---- entered savedCard ----");
      this.savedTokenizedCard = true;
      console.info(event.data);
      // Save credit card and customer info after tokenize
      updateCustomerCreditCardData(
        {},
        {
          propertyTicker: this.props.propertyTicker,
          customerId: event.data.customer.external_id,
          customerCreditCardData: {
            customer: event.data.customer,
            source: event.data.source,
          },
        }
      ).then(() => {
        // Start payment's process
        const orderId = generateBase58uuid();
        this.props.createPnPV2PaymentOrder({
          propertyTicker: this.props.propertyTicker,
          reservationId: this.reservationId,
          amount: this.props.amount,
          orderId,
        });
      });
    }
  };

  errorServer = event => {
    this.props.tokenizePnPV2Error(event.data);
    const additionalData = {
      propertyTicker: this.props.propertyTicker,
      paymentTicker: this.props.paymentTicker,
    };
    createPnPErrorLog({
      reservationId: this.reservationId,
      rawEvent: event
        ? JSON.stringify({
            type: event.type,
            message: event.message,
            data: event.data,
            additionalData,
          })
        : JSON.stringify({ additionalData }),
      message: "PNP Server error",
    });
  };

  render() {
    return (
      <PnPV2Form id="PnPV2-form">
        {this.props.creditCardTypes && (
          <CreditCardIcons creditCardTypes={this.props.creditCardTypes} />
        )}

        <StyledDescription>{this.props.description}</StyledDescription>
      </PnPV2Form>
    );
  }
}

PayNoPainV2Bridge.propTypes = {
  propertyTicker: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  idReservation: PropTypes.string.isRequired,
  paymentTicker: PropTypes.string.isRequired,
  amount: PropTypes.number.isRequired,
  creditCardTypes: PropTypes.array,
  createPnPV2PaymentOrder: PropTypes.func.isRequired,
  tokenizePnPV2Error: PropTypes.func.isRequired,
  handlePaymentFailure: PropTypes.func.isRequired,
  pnpV2PaymentToken: PropTypes.bool,
  handleModalContent: PropTypes.func,
  handleCloseModal: PropTypes.func,
  handleSuccess: PropTypes.func,
  params: PropTypes.object,
  createPnPV2PaymentOrderSuccess: PropTypes.func,
  createPnPV2PaymentOrderError: PropTypes.func,
  template: PropTypes.string.isRequired,
};

const PayNoPainV2BridgeWrapper = props => {
  const dispatch = useDispatch();
  return <PayNoPainV2Bridge {...props} dispatch={dispatch} />;
};

export default PayNoPainV2BridgeWrapper;
